/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { CSSProperties } from 'react';
import { types as repr } from '@sitebuilder/core';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import { applyPageCss } from '../utils';
import pageStyles from './Page.styles';
import Layout, { LayoutType } from './Layout';

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, repr.PageLayoutOptions {
    pageName: string;
    overflowOption: 'hidden' | 'noContinue' | 'continue';
    includeHeader?: boolean;
    includeFooter?: boolean;
    isSource?: boolean;
    isTarget?: boolean;
    chainFlowName?: string;
    pageLayout: LayoutType;
    leftColumnOptions: CSSProperties;
    rightColumnOptions: CSSProperties;
}

const Page = (props: commonTypes.WidgetInstanceProps) => {
    const widgetInstance = commonHooks.useWidgetInstance(props.widgetInstanceId);
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const { styles, pageName, includeHeader, includeFooter, pageLayout, ...rest } =
        widgetProperties;
    const appContext = commonHooks.useAppContext();
    const { inEditMode, Placeholder } = appContext;
    const { data } = props;

    let cssStyles = '';

    cssStyles += `.${pageName || 'page'} {
        page: ${pageName}Group;
    }`;

    cssStyles += `@page ${pageName}Group {`;

    const layoutOptions: repr.PageLayoutOptions = {
        pageSize: rest.pageSize,
        pageWidth: rest.pageWidth,
        pageHeight: rest.pageHeight,
        pageOrientation: rest.pageOrientation,
        pageMargin: rest.pageMargin,
        pagePadding: rest.pagePadding,
        pageBleed: rest.pageBleed,
    };

    // Get page dimensions based on size and orientation
    const getPageDimensions = (
        size: string,
        orientation: string,
        customWidth?: number,
        customHeight?: number
    ) => {
        const dimensions = {
            letter: { width: 8.5, height: 11 },
            A4: { width: 8.27, height: 11.7 },
            legal: { width: 8.5, height: 14 },
        };

        let baseDims = dimensions[size as keyof typeof dimensions] || dimensions.letter;

        if (size === 'Custom' && customWidth && customHeight) {
            baseDims = { width: customWidth, height: customHeight };
        }

        if (orientation === 'landscape') {
            return { width: baseDims.height, height: baseDims.width };
        }
        return baseDims;
    };

    const pageDimensions = getPageDimensions(
        layoutOptions.pageSize || 'letter',
        layoutOptions.pageOrientation || 'portrait',
        layoutOptions.pageWidth,
        layoutOptions.pageHeight
    );

    cssStyles += applyPageCss({
        size: layoutOptions.pageSize,
        orientation: layoutOptions.pageOrientation,
        width: layoutOptions.pageWidth,
        height: layoutOptions.pageHeight,
        margin: layoutOptions.pageMargin,
        padding: layoutOptions.pagePadding || { top: 0, left: 0, bottom: 0, right: 0 },
        bleed: layoutOptions.pageBleed,
    });

    // page height to hide overflow content based on actual page dimensions
    const pageHeight = pageDimensions.height;

    if (includeHeader) {
        // Page header styles
        cssStyles += `@top-center {
            content: element(page-header);
            -ro-pdf-tag-type: 'Div';
        }`;
    }

    if (includeFooter) {
        // Page footer styles
        cssStyles += `@bottom-center {
            content: element(page-footer);
            -ro-pdf-tag-type: 'Div';
        }`;
    }

    cssStyles += '}';

    if (includeHeader) {
        cssStyles += `.page-header {
            position: running(page-header);
        } `;
    }

    if (includeFooter) {
        cssStyles += `.page-footer {
            position: running(page-footer);
        } `;
    }
    const { top, bottom } = layoutOptions.pageMargin || { top: 0.4, bottom: 0.4 };

    if (layoutOptions.pageMargin) {
        cssStyles += `.page-body {`;
        if (rest?.overflowOption === 'hidden') {
            cssStyles += `
            height: ${pageHeight - (top + bottom)}in;
            page-break-inside: avoid;
            break-inside: avoid;
            break-after: avoid;
            page-break-after: avoid;
            overflow: hidden;
        `;
        } else if (rest?.overflowOption === 'noContinue') {
            cssStyles += `
            page-break-inside: auto;
            break-inside: auto;
            break-after: page;
            page-break-after: page;
        `;
        }
    }

    cssStyles += '}';

    if (rest?.overflowOption === 'continue') {
        if (rest?.isSource) {
            cssStyles += `
                div[data-region-flow-into="${rest?.chainFlowName}"] {
                    -ro-flow-into: ${rest?.chainFlowName}ContentFlow content;
                }
            `;
        }
        cssStyles += `
            div[data-region-flow-from="${rest?.chainFlowName}"] {
                -ro-flow-from: ${rest?.chainFlowName}ContentFlow;
            }
        `;
    }

    // add page styles for edit and web view mode
    // so that page has default style when empty widget is dropped and
    // it looks same in actual PDF and PPTX modes
    const editPageStyle = {
        margin: '1em 0',
        boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
        height: `${pageDimensions.height - (top + bottom)}in`,
        width: `${pageDimensions.width}in`,
    };

    // add page body styles for edit and web view mode
    // so that page has default style when empty widget is dropped and
    // it looks same in actual PDF and PPTX modes
    const editPageBodyStyle = {
        padding:
            layoutOptions && layoutOptions.pagePadding
                ? // eslint-disable-next-line max-len
                  `${layoutOptions.pagePadding.top}in ${layoutOptions.pagePadding.left}in ${layoutOptions.pagePadding.bottom}in ${layoutOptions.pagePadding.right}in`
                : 0,
        margin:
            layoutOptions && layoutOptions.pageMargin
                ? // eslint-disable-next-line max-len
                  `${layoutOptions.pageMargin.top}in ${layoutOptions.pageMargin.left}in ${layoutOptions.pageMargin.bottom}in ${layoutOptions.pageMargin.right}in`
                : 0,
    };

    return (
        <div
            css={css`
                ${cssStyles || ''}${pageStyles}${styles || ''}
            `}
            className={`page-widget`}
            style={
                inEditMode || !(appContext.isDocumentTypePdf || appContext.exportType)
                    ? editPageStyle
                    : {}
            }
        >
            <div className={`${pageName || 'page'}`}>
                {includeHeader ? (
                    <div className="page-header">
                        <Placeholder
                            contentPlaceHolderId={'page-header'}
                            parentWidgetInstanceId={widgetInstance?.id}
                            data={data}
                        />
                        {inEditMode && (
                            <div className="page-header-edit-mode">
                                <div className="border" />
                                <span>Add header components above this</span>
                            </div>
                        )}
                    </div>
                ) : null}
                {rest?.isTarget && rest?.overflowOption === 'continue' ? (
                    <div data-region-flow-from={rest?.chainFlowName}>&nbsp;</div>
                ) : null}
                <div
                    className="page-body page-layout"
                    data-region-flow-into={rest?.chainFlowName}
                    style={
                        inEditMode || !(appContext.isDocumentTypePdf || appContext.exportType)
                            ? editPageBodyStyle
                            : {}
                    }
                >
                    <Layout
                        data={data}
                        layout={pageLayout}
                        parentWidgetInstanceId={widgetInstance?.id}
                        leftColumnOptions={rest.leftColumnOptions}
                        rightColumnOptions={rest.rightColumnOptions}
                    />
                </div>
                {rest?.isSource && rest?.overflowOption === 'continue' ? (
                    <div data-region-flow-from={rest?.chainFlowName}>&nbsp;</div>
                ) : null}
                {includeFooter ? (
                    <div className="page-footer">
                        {inEditMode && (
                            <div className="page-footer-edit-mode">
                                <div className="border" />
                                <span>Add footer components below this</span>
                            </div>
                        )}
                        <Placeholder
                            contentPlaceHolderId={'page-footer'}
                            parentWidgetInstanceId={widgetInstance?.id}
                            data={data}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default Page;
